import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import { Search, Bell, X, Menu, UserPlus, DollarSign, Award, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import SidePanel from './SidePanel';
import TdsReportContent from './admin/TdsReportContent';

// Icon mapping for notifications
const getNotificationIcon = (type) => {
  switch (type) {
    case 'rank': return Award;
    case 'referral': return UserPlus;
    case 'earning': return DollarSign;
    case 'reward': return Award;
    default: return Bell;
  }
};

const Layout = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Side Panel State
  const [isTdsPanelOpen, setIsTdsPanelOpen] = useState(false);

  // Responsive state - default sidebar closed on mobile
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const [notifications, setNotifications] = useState([]);

  const fetchNotifications = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      const response = await apiService.getNotifications();
      if (response.success) {
        // Map raw backend data to UI-friendly format with titles and links
        const mappedNotifications = (response.notifications || []).map(n => {
          let title = 'System Update';
          let link = '/dashboard';
          const type = (n.type || '').toLowerCase();

          switch (type) {
            case 'earning':
              title = 'Earnings Credited';
              link = '/earnings';
              break;
            case 'referral':
              title = 'New Team Member';
              link = '/network/downlines';
              break;
            case 'rank':
              title = 'Rank Achieved!';
              link = '/dashboard';
              break;
            case 'payout':
              title = 'Binary Payout';
              link = '/earnings';
              break;
            case 'reward':
              title = 'New Reward Claimed';
              link = '/dashboard';
              break;
            default:
              title = n.title || 'Notification';
              link = '/dashboard';
              break;
          }

          return {
            ...n,
            type: type, // Use normalized lowercase type
            title: title,
            link: link,
            read: n.is_read === 1 || n.is_read === '1' || n.is_read === true
          };
        });
        setNotifications(mappedNotifications);
      }
    } catch (error) {
      console.error("Failed to fetch notifications", error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user]);

  // Search data - Role specific
  const getRoleBasedSearchData = () => {
    const role = user?.role || 'user'; // Default to user if not found

    const common = [
      { type: 'feature', name: 'Profile Settings', path: '/profile', description: 'Edit your profile information' },
      { type: 'feature', name: 'Notifications', path: '#notifications', description: 'View all notifications' },
      { type: 'feature', name: 'Logout', path: '#logout', description: 'Sign out of your account' },
    ];

    const userPages = [
      { type: 'page', name: 'Bank Details', path: '/profile/bank', description: 'Manage your bank account and withdrawals' },
      { type: 'page', name: 'Dashboard', path: '/dashboard', description: 'View your overview and statistics' },
      { type: 'page', name: 'Earnings', path: '/earnings', description: 'Track your income and transactions' },
      { type: 'page', name: 'My Team', path: '/network/downlines', description: 'View your referral team structure' },
      { type: 'page', name: 'Packages', path: '/products/packages', description: 'Browse and purchase packages' },
      { type: 'feature', name: 'Direct Income', path: '/earnings', description: 'View direct referral income' },
      { type: 'feature', name: 'Withdraw Funds', path: '/profile/bank', description: 'Withdraw money to your bank' },
    ];

    const adminPages = [
      { type: 'page', name: 'Admin Dashboard', path: '/dashboard', description: 'System overview' },
      { type: 'page', name: 'All Users', path: '/admin/users', description: 'Manage registered users' },
      { type: 'page', name: 'Teams & Trees', path: '/admin/teams', description: 'View genealogy trees' },
      { type: 'page', name: 'Pending Approvals', path: '/admin/approvals', description: 'Approve KYC/Withdrawals' },
      { type: 'page', name: 'Binary Payouts', path: '/admin/binary-payouts', description: 'Process commissions' },
      { type: 'page', name: 'Transactions', path: '/admin/transactions', description: 'View financial logs' },
      { type: 'page', name: 'Settings', path: '/admin/settings', description: 'System settings' },
    ];

    const franchisePages = [
      { type: 'page', name: 'Franchise Dashboard', path: '/franchise/dashboard', description: 'Franchise overview' },
      { type: 'page', name: 'Buy E-Pins', path: '/franchise/buy-epin', description: 'Purchase E-Pins' },
      { type: 'page', name: 'Inventory', path: '/franchise/stock', description: 'View your E-Pins' },
      { type: 'page', name: 'Sales Report', path: '/franchise/sales-report', description: 'View sales history' },
      { type: 'page', name: 'Commissions', path: '/franchise/commission', description: 'View commission earnings' },
      { type: 'page', name: 'Franchise Profile', path: '/franchise/profile', description: 'Manage franchise profile' },
    ];

    let data = [];
    if (role === 'admin') data = [...common, ...adminPages];
    else if (role === 'franchise') data = [...common, ...franchisePages];
    else data = [...common, ...userPages];

    return data.sort((a, b) => a.name.localeCompare(b.name));
  };

  const searchData = getRoleBasedSearchData();

  const handleSearch = (query) => {
    setSearchQuery(query);
    setShowSearchResults(query.length > 0);
  };

  const filteredResults = searchData.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSearchResultClick = (path) => {
    if (path.startsWith('#')) {
      if (path === '#notifications') {
        setShowNotifications(true);
      } else if (path === '#logout') {
        apiService.logout();
        navigate('/login');
      }
    } else {
      navigate(path);
    }
    setSearchQuery('');
    setShowSearchResults(false);
  };

  const [selectedNotification, setSelectedNotification] = useState(null);

  const handleNotificationClick = async (notification) => {
    // Navigate regardless of current read status
    if (notification.link) {
      navigate(notification.link);
    }

    // Show details in modal if truncated or if user wants to 'open' it
    setSelectedNotification(notification);
    setShowNotifications(false);

    // If not read, update it in the database and locally
    if (!notification.read) {
      try {
        await apiService.markNotificationAsRead(notification.id);
        setNotifications(prev => prev.map(n =>
          n.id === notification.id ? { ...n, read: true } : n
        ));
      } catch (error) {
        console.error("Failed to mark notification as read", error);
      }
    }
  };

  const clearAllNotifications = async () => {
    try {
      await apiService.markAllNotificationsAsRead();
      setNotifications([]);
    } catch (error) {
      console.error("Failed to clear notifications", error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navbar - Full Width - Fixed - Responsive */}
      <header className="fixed top-0 left-0 right-0 bg-white shadow-sm z-[50] border-b border-gray-200">
        <div className="px-3 sm:px-4 md:px-6 lg:px-8 w-full flex items-center justify-between h-16 md:h-[75px]">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <Menu size={24} />
          </button>

          {/* Logo - Responsive sizing */}
          <div className="flex items-center flex-shrink-0">
            <img
              src="/logo.png"
              alt="SwiftMLM Logo"
              className="h-12 sm:h-16 md:h-20 lg:h-[110px] w-auto max-w-[120px] sm:max-w-[200px] md:max-w-[300px] lg:max-w-[500px] object-contain"
            />
          </div>

          {/* Desktop Search Bar */}
          <div className="hidden md:flex flex-1 max-w-xl relative mx-4 lg:mx-8">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search pages, features..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchQuery && setShowSearchResults(true)}
                className="w-full pl-10 pr-10 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setShowSearchResults(false);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Search Results Dropdown */}
            {showSearchResults && filteredResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-96 overflow-y-auto z-50">
                <div className="p-2">
                  {filteredResults.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => handleSearchResultClick(item.path)}
                      className="p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${item.type === 'page'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700'
                              }`}>
                              {item.type === 'page' ? 'PAGE' : 'FEATURE'}
                            </span>
                            <h4 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors text-sm">
                              {item.name}
                            </h4>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                        </div>
                        <Search size={16} className="text-gray-400 group-hover:text-blue-600 transition-colors" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No Results */}
            {showSearchResults && filteredResults.length === 0 && searchQuery && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 p-6 z-50 text-center">
                <Search size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-gray-600 font-medium">No results found</p>
                <p className="text-gray-400 text-sm mt-1">Try searching for pages or features</p>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4">
            {/* Mobile Search Button */}
            <button
              onClick={() => setShowMobileSearch(!showMobileSearch)}
              className="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Search size={20} />
            </button>

            {/* Notifications Bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Bell size={20} className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1 w-4 h-4 sm:w-5 sm:h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-[10px] sm:text-xs font-bold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown - Responsive */}
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50 max-w-md">
                  <div className="p-3 sm:p-4 bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-between">
                    <h3 className="text-white font-bold text-base sm:text-lg">Notifications</h3>
                    {notifications.length > 0 && (
                      <button
                        onClick={clearAllNotifications}
                        className="text-white text-xs hover:underline"
                      >
                        Clear All
                      </button>
                    )}
                  </div>

                  <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-6 sm:p-8 text-center text-gray-500">
                        <Bell size={48} className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm sm:text-base">No notifications</p>
                      </div>
                    ) : (
                      notifications.map((notification) => {
                        const NotificationIcon = getNotificationIcon(notification.type) || Bell;

                        return (
                          <div
                            key={notification.id}
                            onClick={() => handleNotificationClick(notification)}
                            className={`p-3 sm:p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${!notification.read ? 'bg-blue-50' : ''
                              }`}
                          >
                            <div className="flex items-start space-x-2 sm:space-x-3">
                              <div className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${notification.type === 'rank' ? 'bg-yellow-100' :
                                notification.type === 'referral' ? 'bg-blue-100' :
                                  notification.type === 'earning' ? 'bg-green-100' :
                                    notification.type === 'reward' ? 'bg-purple-100' :
                                      'bg-indigo-100'
                                }`}>
                                <NotificationIcon size={20} className={
                                  notification.type === 'rank' ? 'text-yellow-600' :
                                    notification.type === 'referral' ? 'text-blue-600' :
                                      notification.type === 'earning' ? 'text-green-600' :
                                        notification.type === 'reward' ? 'text-purple-600' :
                                          'text-indigo-600'
                                } />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-900 text-xs sm:text-sm truncate">{notification.title || 'Notification'}</p>
                                <p className="text-[10px] sm:text-xs text-gray-600 mt-1 line-clamp-2">{notification.message}</p>
                                <p className="text-[10px] sm:text-xs text-gray-400 mt-1">
                                  {notification.created_at ? new Date(notification.created_at).toLocaleString() : notification.time}
                                </p>
                              </div>
                              {!notification.read && (
                                <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* User Profile - Responsive */}
            <div
              onClick={() => {
                if (user?.role === 'franchise') {
                  navigate('/franchise/profile');
                } else if (user?.role === 'admin') {
                  // Admin usually doesn't need personal profile
                } else {
                  navigate('/profile');
                }
              }}
              className={`flex items-center space-x-2 sm:space-x-3 bg-gray-50 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl border border-gray-200 ${user?.role !== 'admin' ? 'cursor-pointer hover:bg-gray-100' : 'cursor-default'} transition-colors`}
            >
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg flex-shrink-0 overflow-hidden border border-white/20 shadow-sm">
                {user?.profile_image ? (
                  <img
                    src={user.profile_image.startsWith('http') ? user.profile_image : (user.profile_image.startsWith('data:') ? user.profile_image : `${window.location.origin}${user.profile_image.startsWith('/') ? '' : '/'}${user.profile_image}`)}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = (user?.name || user?.full_name || user?.user_id || 'AD').charAt(0).toUpperCase();
                    }}
                  />
                ) : (
                  (user?.name || user?.full_name || user?.user_id || 'AD').charAt(0).toUpperCase()
                )}
              </div>
              <div className="text-left hidden sm:block">
                <p className="text-xs sm:text-sm font-semibold text-gray-800 truncate max-w-[100px] md:max-w-[150px]">
                  {user?.name || user?.full_name || 'Admin User'}
                </p>
                <p className="text-[10px] sm:text-xs text-gray-500 truncate">
                  {user?.role === 'admin' ? 'Administrator' : `ID: ${user?.user_id || user?.referral_code || '---'}`}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Search Bar - Collapsible */}
        {showMobileSearch && (
          <div className="md:hidden px-3 pb-3 border-t border-gray-100">
            <div className="relative mt-3">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search pages, features..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchQuery && setShowSearchResults(true)}
                className="w-full pl-10 pr-10 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                autoFocus
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setShowSearchResults(false);
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Mobile Search Results */}
            {showSearchResults && filteredResults.length > 0 && (
              <div className="mt-2 bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto">
                <div className="p-1">
                  {filteredResults.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => {
                        handleSearchResultClick(item.path);
                        setShowMobileSearch(false);
                      }}
                      className="p-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                    >
                      <div className="flex items-start space-x-2">
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0 ${item.type === 'page'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-purple-100 text-purple-700'
                          }`}>
                          {item.type === 'page' ? 'PAGE' : 'FEATURE'}
                        </span>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 text-xs truncate">{item.name}</h4>
                          <p className="text-[10px] text-gray-500 mt-0.5 line-clamp-1">{item.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </header>

      {/* Sidebar and Main Content - Responsive */}
      <div className="flex pt-16 md:pt-[75px] overflow-x-hidden min-h-screen">
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onOpen={() => setSidebarOpen(true)}
          onOpenTds={() => setIsTdsPanelOpen(true)}
        />
        <main className={`flex-1 p-3 sm:p-4 md:p-6 lg:p-8 transition-all duration-300 overflow-x-hidden w-full ${sidebarOpen ? 'lg:ml-72' : 'lg:ml-12'}`}>
          <div className="max-w-[1600px] mx-auto">
            {children}
          </div>
        </main>
      </div>

      {/* TDS Master Report Side Panel */}
      {user?.role === 'admin' && (
        <SidePanel
          isOpen={isTdsPanelOpen}
          onClose={() => setIsTdsPanelOpen(false)}
          title="TDS Master Report"
        >
          {isTdsPanelOpen && <TdsReportContent isPanel={true} />}
        </SidePanel>
      )}



      {/* Click outside to close notifications */}
      {showNotifications && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowNotifications(false)}
        ></div>
      )}

      {/* Selected Notification Detail Modal */}
      {selectedNotification && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedNotification(null)} />
          <div className="relative bg-white border border-gray-100 rounded-2xl w-full max-w-lg p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                  <Info size={20} />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{selectedNotification.title || 'Notification Details'}</h3>
              </div>
              <button onClick={() => setSelectedNotification(null)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                <p className="text-gray-800 leading-relaxed text-sm whitespace-pre-wrap">
                  {selectedNotification.message}
                </p>
              </div>

              <div className="flex items-center justify-between text-xs text-gray-500">
                <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-md">
                  Type: <span className="capitalize font-semibold">{selectedNotification.type}</span>
                </span>
                <span>{selectedNotification.created_at ? new Date(selectedNotification.created_at).toLocaleString() : selectedNotification.time}</span>
              </div>

              <div className="pt-2">
                <button
                  onClick={() => setSelectedNotification(null)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition shadow-lg shadow-blue-500/20"
                >
                  Close Message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
